import {type Config, media, type SpacerObject} from '@nfq/react-grid';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';

import {configType} from 'UI/utils/globalStyles';

/**
 * Custom hook for creating and configuring an Embla carousel slider with responsive breakpoints.
 * This hook initializes an Embla carousel with specific settings for different screen sizes and provides
 * the necessary references and API for controlling the slider functionality. The hook automatically
 * generates CSS grid queries based on the provided slide count configuration for responsive behavior.
 *
 * @param autoplay      Optional boolean to enable automatic slide progression through the carousel.
 * @param autoplaySpeed Optional number specifying the delay in milliseconds between automatic slide transitions.
 * @param loop          Optional boolean to enable infinite loop functionality for the carousel slides.
 * @param slideCount    Optional configuration object defining how many slides to show per breakpoint.
 * @returns An object containing the embla reference, API for carousel control, and CSS for responsive grid layout.
 *
 * @example
 * ```tsx
 * const MySlider = () => {
 *     const { emblaRef, emblaApi, containerCss } = useSlider(
 *         true,
 *         3000,
 *         false,
 *         {
 *             xs: 1,
 *             md: 2,
 *             lg: 3
 *         }
 *     );
 *
 *     return (
 *         <div className="embla" ref={emblaRef}>
 *             <div className="embla__container" css={containerCss}>
 *                 <div className="embla__slide">Slide 1</div>
 *                 <div className="embla__slide">Slide 2</div>
 *                 <div className="embla__slide">Slide 3</div>
 *             </div>
 *         </div>
 *     );
 * };
 * ```
 */
export const useSlider = (
    autoplay?: boolean | null,
    autoplaySpeed?: number | null,
    loop?: boolean,
    slideCount?: SpacerObject
) => {
    const configBreakpoints = (configType as Config<'light'>).breakpoints;
    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: loop ? 'center' : 'start',
        breakpoints: Object.fromEntries(Object.keys(slideCount ?? {xs: 1}).map(breakpoint => {
            // eslint-disable-next-line no-param-reassign
            const slides = slideCount ?? {xs: 1};

            if (slides[breakpoint as keyof SpacerObject]) {
                // eslint-disable-next-line security/detect-object-injection
                return [
                    `(min-width: ${configBreakpoints?.[breakpoint as keyof SpacerObject]}px)`,
                    {slidesToScroll: slides[breakpoint as keyof SpacerObject]}
                ];
            }

            return null;
        }).filter(Boolean).reverse()),
        loop: loop ?? false
    }, autoplay ? [Autoplay({
        // eslint-disable-next-line @nfq/no-magic-numbers
        delay: autoplaySpeed ?? 4000,
        stopOnInteraction: true
    })] : []);
    let queries = '';

    if (slideCount) {
        queries = Object.keys(slideCount).map(breakpoint => {
            if (slideCount[breakpoint as keyof SpacerObject]) {
                return `
                ${media(breakpoint as keyof SpacerObject)} {
                    grid-auto-columns: calc(100% / ${slideCount[breakpoint as keyof SpacerObject]} - 24px * calc(${slideCount[breakpoint as keyof SpacerObject]} - 1) / ${slideCount[breakpoint as keyof SpacerObject]});
                }`;
            }

            return null;
        }).filter(Boolean)
            .reverse()
            .join('\n');
    } else {
        queries = 'grid-auto-columns: auto;';
    }

    return {
        containerCss: queries,
        emblaApi,
        emblaRef
    };
};